import { formatUsd, NANO_PER_USD } from '@console/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Badge, CopyButton, ErrorText, Field, Notice, PageHeader, Spinner } from '../components/ui/misc'
import { useToast } from '../components/ui/toast'
import { api, useActiveOrg, useMe } from '../lib/api'
import { fmtDate, fmtRelative } from '../lib/format'

interface Key {
  id: string
  name: string
  prefix: string
  createdBy: string | null
  lastUsedAt: string | null
  revokedAt: string | null
  createdAt: string
  budgetMonthlyNano: number | null
  spend?: { monthNano: number }
}

const usd = (n: number | null) => (n == null ? '' : String(n / NANO_PER_USD))
const toNum = (s: string) => (s.trim() === '' ? null : Number(s))

/** This month's spend against the key's monthly cap. */
function BudgetCell({ k }: { k: Key }) {
  const spent = k.spend?.monthNano ?? 0
  const cap = k.budgetMonthlyNano
  if (cap == null) return <span className="text-ink-muted">{formatUsd(spent)} this month</span>
  const used = Math.min(1, spent / cap)
  return (
    <div className="flex items-center gap-2.5">
      <span className="tabular-nums text-ink-secondary">
        {formatUsd(spent)} <span className="text-ink-muted">of {formatUsd(cap)}</span>
      </span>
      <div className="h-1 w-16 overflow-hidden rounded-full bg-surface-sunken">
        <div className={`h-full rounded-full ${used >= 1 ? 'bg-danger' : used >= 0.8 ? 'bg-warning' : 'bg-brand'}`} style={{ width: `${used * 100}%` }} />
      </div>
    </div>
  )
}

export function ApiKeysPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data: me } = useMe()
  const { can, org } = useActiveOrg()
  const canCreate = can('keys.create')
  const canManageKeys = can('keys.manage')
  const { data, isLoading } = useQuery({ queryKey: ['keys'], queryFn: () => api<{ data: Key[] }>('/api/keys') })
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState('')
  const [newBudget, setNewBudget] = useState('')
  const [budgetFor, setBudgetFor] = useState<Key | null>(null)
  const [budget, setBudget] = useState('')
  const [created, setCreated] = useState<{ name: string; key: string } | null>(null)
  const [revoking, setRevoking] = useState<Key | null>(null)
  const [showRevoked, setShowRevoked] = useState(false)

  const create = useMutation({
    mutationFn: () =>
      api<{ key: string; name: string }>('/api/keys', {
        body: { name, budgetMonthlyUsd: toNum(newBudget) },
      }),
    onSuccess: (r) => {
      setCreated(r)
      setCreateOpen(false)
      setName('')
      setNewBudget('')
      void qc.invalidateQueries({ queryKey: ['keys'] })
    },
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setRevoking(null)
      toast('Key revoked')
      void qc.invalidateQueries({ queryKey: ['keys'] })
    },
  })

  const saveBudget = useMutation({
    mutationFn: () => api(`/api/keys/${budgetFor!.id}/budget`, { method: 'PUT', body: { monthlyUsd: toNum(budget) } }),
    onSuccess: () => {
      setBudgetFor(null)
      toast('Key budget saved')
      void qc.invalidateQueries({ queryKey: ['keys'] })
    },
  })

  const keys = (data?.data ?? []).filter((k) => showRevoked || !k.revokedAt)
  const revokedCount = (data?.data ?? []).filter((k) => k.revokedAt).length

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader
        title="API Keys"
        actions={
          canCreate && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> Create key
            </Button>
          )
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-8">
        {isLoading ? (
          <Spinner />
        ) : keys.length === 0 ? (
          <p className="text-[13px] text-ink-muted">No API keys yet.</p>
        ) : (
          <div className="-mx-3 overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead className="border-b border-line">
                <tr>
                  <th className="th">Name</th>
                  <th className="th">Key</th>
                  <th className="th">Created</th>
                  <th className="th">Last used</th>
                  <th className="th">This month</th>
                  <th className="th">Status</th>
                  <th className="th" />
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const mine = k.createdBy === me?.user?.id
                  return (
                    <tr key={k.id} className="border-b border-line-subtle last:border-0">
                      <td className="td font-medium">{k.name}</td>
                      <td className="td font-mono text-[11.5px] text-ink-secondary">{k.prefix}…</td>
                      <td className="td text-ink-secondary">{fmtDate(k.createdAt)}</td>
                      <td className="td text-ink-secondary">{k.lastUsedAt ? fmtRelative(k.lastUsedAt) : 'Never'}</td>
                      <td className="td">
                        <BudgetCell k={k} />
                      </td>
                      <td className="td">{k.revokedAt ? <Badge tone="danger">Revoked</Badge> : <Badge tone="success">Active</Badge>}</td>
                      <td className="td whitespace-nowrap text-right">
                        {!k.revokedAt && canManageKeys && (
                          <Button
                            variant="ghost"
                            className="!h-7 !text-[12px]"
                            onClick={() => {
                              setBudgetFor(k)
                              setBudget(usd(k.budgetMonthlyNano))
                            }}
                          >
                            Budget
                          </Button>
                        )}
                        {!k.revokedAt && (canManageKeys || (mine && canCreate)) && (
                          <Button variant="ghost" className="!h-7 !text-[12px] text-danger" onClick={() => setRevoking(k)}>
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {revokedCount > 0 && (
          <button type="button" className="mt-3 text-[12px] text-ink-muted hover:text-ink" onClick={() => setShowRevoked((v) => !v)}>
            {showRevoked ? 'Hide' : 'Show'} {revokedCount} revoked key{revokedCount === 1 ? '' : 's'}
          </button>
        )}
      </div>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create API key"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!name.trim()}>
              Create key
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Name" hint="Where it will be used, e.g. “production evals” or “staging agent”.">
            <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && create.mutate()} />
          </Field>
          <Field label="Monthly budget (USD)">
            <input className="input" type="number" min={0} step="0.01" placeholder="No cap" value={newBudget} onChange={(e) => setNewBudget(e.target.value)} />
          </Field>
        </div>
        <div className="mt-3">
          <ErrorText error={create.error} />
        </div>
      </Modal>

      <Modal open={!!created} onClose={() => setCreated(null)} title="Save your key" footer={<Button onClick={() => setCreated(null)}>Done</Button>}>
        <Notice tone="warning">This is the only time the full key is shown. Copy it now and keep it secret.</Notice>
        <div className="mt-3 flex items-center gap-2 rounded-md border border-line bg-surface-sunken px-3 py-2">
          <code className="min-w-0 flex-1 break-all text-[12px] text-ink">{created?.key}</code>
          <CopyButton value={created?.key ?? ''} />
        </div>
      </Modal>

      <Modal
        open={!!budgetFor}
        onClose={() => setBudgetFor(null)}
        title={`Monthly budget for ${budgetFor?.name ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBudgetFor(null)}>
              Cancel
            </Button>
            <Button onClick={() => saveBudget.mutate()} loading={saveBudget.isPending}>
              Save
            </Button>
          </>
        }
      >
        <Field label="Monthly cap (USD)">
          <input className="input" type="number" min={0} step="0.01" autoFocus placeholder="No cap" value={budget} onChange={(e) => setBudget(e.target.value)} />
        </Field>
        <div className="mt-3">
          <ErrorText error={saveBudget.error} />
        </div>
      </Modal>

      <Modal
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title="Revoke key?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={revoke.isPending} onClick={() => revoking && revoke.mutate(revoking.id)}>
              Revoke
            </Button>
          </>
        }
      >
        Requests using <b className="text-ink">{revoking?.name}</b> will fail with 401 within seconds. This cannot be undone.
      </Modal>
    </div>
  )
}
